import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO, { reviewSchema } from "@/components/SEO";

export default function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name:"", role:"", venue:"", rating:5, review:"" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/testimonials").then(r => setItems(r.data)).catch(()=>{}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/testimonials/submit", f);
      toast.success("Thank you! Your testimonial is pending approval.");
      setF({ name:"", role:"", venue:"", rating:5, review:"" });
    } catch { toast.error("Could not submit. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="testimonials-page" className="pt-32">
      <SEO title="Testimonials" path="/testimonials" description="Stories from real couples we've had the privilege of capturing — honest words about how their wedding felt and how we cared for it."
           jsonLd={items.length ? reviewSchema(items.slice(0, 10)) : null} />
      <section className="section-pad pb-12 max-w-[1400px] mx-auto text-center">
        <div className="eyebrow">Testimonials</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light mt-4 tracking-tight">Stories From <em className="text-[#D4AF37] not-italic">Our Couples</em></h1>
      </section>
      <section className="px-6 md:px-10 max-w-[1400px] mx-auto pb-20 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(t => (
          <div key={t.id} className="bg-[#0a0a0a] border border-white/10 p-8" data-testid={`testimonial-${t.id}`}>
            <div className="flex gap-1 mb-4">{[...Array(t.rating)].map((_,i)=>(<Star key={i} size={14} className="text-[#D4AF37] fill-[#D4AF37]" />))}</div>
            <p className="font-serif text-lg leading-relaxed italic">"{t.review}"</p>
            <div className="mt-6 flex items-center gap-3">
              {t.photo_url && <img src={t.photo_url} alt="" className="w-12 h-12 rounded-full object-cover border border-[#D4AF37]" />}
              <div>
                <div className="font-serif text-base">{t.name}</div>
                <div className="text-[0.65rem] tracking-[0.25em] uppercase text-white/45">{t.role} {t.venue && `· ${t.venue}`}</div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="section-pad bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="eyebrow">Share Your Story</div>
            <h2 className="font-serif text-4xl mt-3 tracking-tight">Tell Us About <em className="text-[#D4AF37] not-italic">Your Day</em></h2>
            <p className="text-white/55 text-sm mt-3">We read every story before it appears here. Stories shared here are not posted to Google.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="testimonial-form">
            {[["name","Your Name *"],["role","Role (Bride / Groom)"],["venue","Venue"]].map(([k,l]) => (
              <input key={k} placeholder={l} required={l.includes("*")} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})}
                data-testid={`ts-${k}`}
                className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none" />
            ))}
            <div className="flex items-center gap-2">
              <span className="text-xs tracking-[0.2em] uppercase text-white/60">Rating:</span>
              {[1,2,3,4,5].map(r => (
                <button type="button" key={r} onClick={()=>setF({...f,rating:r})}>
                  <Star size={20} className={r <= f.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/30"} />
                </button>
              ))}
            </div>
            <textarea placeholder="Your story *" required value={f.review} onChange={(e)=>setF({...f,review:e.target.value})}
              data-testid="ts-review" rows={5}
              className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none" />
            <button type="submit" disabled={busy} className="btn-gold" data-testid="ts-submit">{busy ? "Submitting..." : "Share Your Story"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
